import { useState, useMemo, useEffect } from "react";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, Tooltip,
  LineChart, Line, Legend, ReferenceLine, LabelList
} from "recharts";

// ─── OSS 数据地址 ──────────────────────────────────────────────────────────
const OSS_BASE   = "https://zhongou-fund-daily-market-update.oss-cn-shanghai.aliyuncs.com";
const URL_PERF   = `${OSS_BASE}/data/fund-compare/bonds_tier1.json`;
const URL_NAV    = `${OSS_BASE}/data/fund-compare/nav_series.json`;   // iFinD接口恢复后启用

// ─── CSS 变量 ──────────────────────────────────────────────────────────────
const CSS_VARS = {
  "--gold":    "#D4AA4F",
  "--navy":    "#1B3664",
  "--bg":      "#091428",
  "--text":    "#E8E6E1",
  "--dim":     "#7A8BA5",
  "--border":  "rgba(212,170,79,0.12)",
  "--surface": "rgba(27,54,100,0.20)",
  "--green":   "#5CB88A",
  "--red":     "#E07B54",
};

const COLORS = ["#D4AA4F","#4A90D9","#E07B54","#5CB88A","#9B7ED8","#45B5C4","#D4729B","#7C8FA6"];

const f2   = v => v == null ? "—" : Number(v).toFixed(2);
const fPct = v => v == null ? "—" : Number(v).toFixed(2) + "%";

// ─── 数据加载 Hook ─────────────────────────────────────────────────────────
function useOSSData() {
  const [data,    setData]    = useState(null);
  const [navData, setNavData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 主数据（必须）
        const r = await fetch(URL_PERF);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) setData(j);

        // 净值数据（可选，失败不影响主页面）
        try {
          const rn = await fetch(URL_NAV);
          if (rn.ok) {
            const jn = await rn.json();
            if (!cancelled) setNavData(jn);
          }
        } catch (_) {}

      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { data, navData, loading, error };
}

// ─── Loading / Error ───────────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{height:"60vh",display:"flex",flexDirection:"column",
      alignItems:"center",justifyContent:"center",gap:12}}>
      <div style={{width:32,height:32,borderRadius:"50%",border:"3px solid rgba(212,170,79,0.2)",
        borderTop:"3px solid #D4AA4F",animation:"spin 0.8s linear infinite"}}/>
      <div style={{fontSize:12,color:"var(--dim)"}}>加载数据中…</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function ErrorScreen({ msg }) {
  return (
    <div style={{height:"60vh",display:"flex",flexDirection:"column",
      alignItems:"center",justifyContent:"center",gap:8}}>
      <div style={{fontSize:14,color:"var(--red)"}}>数据加载失败</div>
      <div style={{fontSize:11,color:"var(--dim)"}}>{msg}</div>
    </div>
  );
}

// ─── 广告卡 ────────────────────────────────────────────────────────────────
function AdCard({ zo }) {
  if (!zo) return null;
  return (
    <div style={{borderRadius:10,padding:"22px 26px",marginBottom:18,
      position:"relative",overflow:"hidden",
      background:"linear-gradient(135deg,rgba(27,54,100,0.6) 0%,rgba(212,170,79,0.06) 100%)",
      border:"1px solid rgba(212,170,79,0.3)",borderLeft:"4px solid #D4AA4F"}}>
      <div style={{position:"absolute",right:-20,top:-20,width:160,height:160,borderRadius:"50%",
        background:"radial-gradient(circle,rgba(212,170,79,0.05) 0%,transparent 70%)",pointerEvents:"none"}}/>
      <div style={{display:"flex",alignItems:"flex-start",gap:20,flexWrap:"wrap"}}>

        {/* 产品信息 */}
        <div style={{minWidth:170}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <div style={{width:30,height:30,borderRadius:6,flexShrink:0,
              background:"linear-gradient(135deg,#1B3664,#D4AA4F)",
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:13,fontWeight:900,color:"white"}}>中</div>
            <div>
              <div style={{fontSize:10,color:"var(--gold)",fontWeight:600,letterSpacing:"0.05em"}}>中欧基金</div>
              <div style={{fontSize:9,color:"var(--dim)"}}>ZHONGOUFUND</div>
            </div>
          </div>
          <div style={{fontSize:13,fontWeight:700,lineHeight:1.4,marginBottom:4}}>{zo.name}</div>
          <div style={{fontSize:10,color:"var(--dim)"}}>{zo.code} · 成立 {zo.inception}</div>
          {zo.manager && <div style={{fontSize:10,color:"var(--dim)",marginTop:2}}>基金经理：{zo.manager}</div>}
        </div>

        <div style={{width:1,background:"rgba(212,170,79,0.18)",alignSelf:"stretch",minHeight:60}}/>

        {/* 广告词 */}
        <div style={{flex:1,minWidth:200}}>
          <div style={{fontSize:13,fontWeight:600,color:"var(--gold)",marginBottom:10}}>
            {zo.ad_title}
          </div>
          {(zo.ad_bullets || []).map((b, i) => (
            <div key={i} style={{display:"flex",alignItems:"center",gap:8,
              fontSize:12,color:"var(--dim)",marginBottom:5}}>
              <div style={{width:4,height:4,borderRadius:"50%",background:"var(--gold)",flexShrink:0}}/>
              {b}
            </div>
          ))}
        </div>

        {/* 优势指标快速预览（Top3） */}
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          {(zo.highlights || []).slice(0, 3).map((h, i) => (
            <div key={i} style={{textAlign:"center",padding:"10px 14px",borderRadius:7,minWidth:82,
              background: i<2 ? "rgba(212,170,79,0.1)" : "rgba(27,54,100,0.35)",
              border: i<2 ? "1px solid rgba(212,170,79,0.3)" : "1px solid var(--border)"}}>
              <div style={{fontSize:20,fontWeight:700,fontVariantNumeric:"tabular-nums",
                color: i<2 ? "var(--gold)" : "var(--text)"}}>
                {h.key && h.key.includes("return") ? fPct(h.value) : f2(h.value)}
              </div>
              <div style={{fontSize:9,color:"var(--dim)",marginTop:2,lineHeight:1.3}}>{h.label}</div>
              <div style={{fontSize:9,fontWeight:700,marginTop:3,
                color: i<2 ? "var(--gold)" : "var(--dim)"}}>
                第{h.rank}/{h.total}名
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}

// ─── SectionCard ──────────────────────────────────────────────────────────
function SectionCard({ title, accent, children, action }) {
  return (
    <div style={{borderRadius:8,border:"1px solid var(--border)",background:"var(--surface)",
      overflow:"hidden",marginBottom:16}}>
      <div style={{padding:"10px 16px",borderBottom:"1px solid var(--border)",
        display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <div style={{width:3,height:16,borderRadius:2,background:accent||"var(--gold)"}}/>
          <span style={{fontSize:13,fontWeight:600}}>{title}</span>
        </div>
        {action}
      </div>
      <div style={{padding:16}}>{children}</div>
    </div>
  );
}

// ─── 优势指标卡片 ──────────────────────────────────────────────────────────
function HighlightGrid({ highlights }) {
  return (
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10}}>
      {(highlights || []).map((h, i) => {
        const isNo1  = h.rank === 1;
        const isTop3 = h.rank <= 3;
        return (
          <div key={i} style={{borderRadius:8,padding:"14px 16px",position:"relative",
            background: isNo1 ? "rgba(212,170,79,0.08)" : "rgba(27,54,100,0.25)",
            border: isNo1 ? "1px solid rgba(212,170,79,0.4)" : "1px solid var(--border)",
            borderTop:`3px solid ${isNo1?"#D4AA4F":isTop3?"#4A90D9":"rgba(74,144,217,0.4)"}`}}>
            {isNo1 && (
              <div style={{position:"absolute",top:8,right:8,fontSize:9,fontWeight:700,
                background:"rgba(212,170,79,0.2)",color:"var(--gold)",borderRadius:3,padding:"1px 5px"}}>
                NO.1
              </div>
            )}
            <div style={{fontSize:22,fontWeight:700,fontVariantNumeric:"tabular-nums",marginBottom:4,
              color: isNo1?"var(--gold)":"var(--text)"}}>
              {h.key && h.key.includes("return") ? fPct(h.value) : f2(h.value)}
            </div>
            <div style={{fontSize:11,color:"var(--dim)",marginBottom:8}}>{h.label}</div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{height:3,flex:1,borderRadius:2,background:"rgba(255,255,255,0.06)",overflow:"hidden"}}>
                <div style={{height:"100%",width:`${h.percentile}%`,borderRadius:2,
                  background: isNo1?"var(--gold)":isTop3?"#4A90D9":"rgba(74,144,217,0.5)"}}/>
              </div>
              <span style={{fontSize:9,fontWeight:600,minWidth:44,textAlign:"right",
                color: isNo1?"var(--gold)":"var(--dim)"}}>
                {h.rank}/{h.total}名
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── 净值走势图 ────────────────────────────────────────────────────────────
function NavChart({ navData, funds, selected }) {
  const zoFund   = funds.find(f => f.is_zo);
  const selFunds = funds.filter(f => selected.includes(f.code) && !f.is_zo);
  const showFunds = zoFund ? [zoFund, ...selFunds] : selFunds;

  // 从navData里提取系列
  const getSeriesMap = () => {
    if (!navData?.funds) return {};
    const m = {};
    showFunds.forEach(f => {
      const entry = navData.funds[f.code];
      if (entry?.series?.length) m[f.code] = entry.series;
    });
    return m;
  };
  const seriesMap = getSeriesMap();

  const hasNavData = Object.keys(seriesMap).length > 0;

  if (!hasNavData) {
    return (
      <div style={{height:200,display:"flex",flexDirection:"column",
        alignItems:"center",justifyContent:"center",gap:8,
        border:"1px dashed var(--border)",borderRadius:8}}>
        <div style={{fontSize:12,color:"var(--dim)"}}>
          {navData === null ? "净值数据加载中…" : "净值数据暂未接入（iFinD接口额度恢复后自动启用）"}
        </div>
        <div style={{fontSize:11,color:"var(--dim)",opacity:0.6}}>
          当前展示Wind指标数据，净值走势数据来源：同花顺 iFinD
        </div>
      </div>
    );
  }

  // 归一化：起点=100
  const allDates = new Set();
  Object.values(seriesMap).forEach(s => s.forEach(p => allDates.add(p.date)));
  const dates = [...allDates].sort();
  const chartData = dates.map(d => {
    const pt = { date: d };
    showFunds.forEach(f => {
      const series = seriesMap[f.code];
      if (!series) return;
      const point = series.find(p => p.date === d);
      const first = series[0]?.nav;
      if (point && first) pt[f.code] = +((point.nav / first) * 100).toFixed(2);
    });
    return pt;
  });

  const tickDates = dates.filter((_, i) => i % Math.ceil(dates.length / 8) === 0);

  const TT = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{background:"rgba(9,20,40,0.95)",border:"1px solid var(--border)",
        borderRadius:6,padding:"10px 14px",fontSize:11}}>
        <div style={{color:"var(--gold)",fontWeight:600,marginBottom:6}}>{label}</div>
        {payload.map((p, i) => {
          const f = showFunds.find(f => f.code === p.dataKey);
          return (
            <div key={i} style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
              <div style={{width:8,height:8,borderRadius:2,background:p.color}}/>
              <span style={{color:"var(--dim)"}}>{f?.name || p.dataKey}</span>
              <span style={{marginLeft:"auto",paddingLeft:12,fontWeight:600}}>{p.value?.toFixed(2)}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={chartData} margin={{top:8,right:16,left:0,bottom:4}}>
          <XAxis dataKey="date" tick={{fontSize:9,fill:"var(--dim)"}} axisLine={false}
            tickLine={false} ticks={tickDates} minTickGap={40}/>
          <YAxis tick={{fontSize:9,fill:"var(--dim)"}} axisLine={false} tickLine={false}
            tickFormatter={v => v.toFixed(0)} width={36} domain={["auto","auto"]}/>
          <Tooltip content={<TT/>}/>
          <ReferenceLine y={100} stroke="rgba(212,170,79,0.2)" strokeDasharray="4 4"/>
          <Legend formatter={v => showFunds.find(f=>f.code===v)?.name||v}
            wrapperStyle={{fontSize:10,paddingTop:8}}/>
          {showFunds.map((f, i) => (
            <Line key={f.code} type="monotone" dataKey={f.code}
              stroke={f.is_zo ? "#D4AA4F" : COLORS[i % COLORS.length]}
              strokeWidth={f.is_zo ? 2.5 : 1.5}
              dot={false} connectNulls/>
          ))}
        </LineChart>
      </ResponsiveContainer>
      <div style={{textAlign:"center",fontSize:10,color:"var(--dim)",marginTop:2}}>
        归一化净值走势（起点=100）· 数据来源：同花顺 iFinD
      </div>
    </div>
  );
}

// ─── 行业排行柱状图 ────────────────────────────────────────────────────────
function PeerBar({ metric, label, funds, higherBetter }) {
  const isDrawdown = metric.includes("drawdown") || metric.includes("volatility");
  const sorted = [...funds]
    .filter(f => f[metric] != null)
    .sort((a, b) => {
      const av = isDrawdown ? Math.abs(a[metric]) : (a[metric]||0);
      const bv = isDrawdown ? Math.abs(b[metric]) : (b[metric]||0);
      return higherBetter ? bv - av : av - bv;
    });
  const displayData = sorted.map(f => ({
    ...f,
    _v: isDrawdown ? Math.abs(f[metric]||0) : (f[metric]||0),
    _raw: f[metric],
  }));
  const max = Math.max(...displayData.map(d => d._v));

  const TT = ({ active, payload }) => {
    if (!active||!payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div style={{background:"rgba(9,20,40,0.95)",border:"1px solid var(--border)",
        borderRadius:6,padding:"8px 12px",fontSize:11}}>
        <div style={{fontWeight:600,color:d.is_zo?"var(--gold)":"var(--text)",marginBottom:3}}>
          {d.name}
        </div>
        <div style={{color:"var(--dim)"}}>
          {label}：<span style={{fontWeight:600,color:"var(--text)"}}>{fPct(d._raw)}</span>
        </div>
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={Math.max(200, sorted.length * 24)}>
      <BarChart data={displayData} layout="vertical" margin={{top:2,right:55,left:0,bottom:2}}>
        <XAxis type="number" tick={{fontSize:9,fill:"var(--dim)"}} axisLine={false}
          tickLine={false} tickFormatter={v=>v.toFixed(2)} domain={[0,max*1.18]}/>
        <YAxis type="category" dataKey="name" tick={{fontSize:10,fill:"var(--dim)"}}
          axisLine={false} tickLine={false} width={100}/>
        <Tooltip content={<TT/>}/>
        <Bar dataKey="_v" radius={[0,3,3,0]}>
          {displayData.map((d, i) => (
            <Cell key={i} fill={d.is_zo?"#D4AA4F":`rgba(74,144,217,${0.3+0.4*(1-i/displayData.length)})`}/>
          ))}
          <LabelList dataKey="_v" position="right"
            formatter={v => v?.toFixed(2)} style={{fontSize:9,fill:"var(--dim)"}}/>
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── 多维雷达 ──────────────────────────────────────────────────────────────
function MetricRadar({ funds, selected }) {
  const zoFund   = funds.find(f => f.is_zo);
  const selFunds = funds.filter(f => selected.includes(f.code) && !f.is_zo);

  const DIMS = [
    {key:"sharpe_3y",   label:"夏普比率",  high:true},
    {key:"sortino_3y",  label:"索提诺",    high:true},
    {key:"calmar_3y",   label:"卡尔玛",    high:true},
    {key:"return_3y",   label:"3年收益",   high:true},
    {key:"alpha_3y",    label:"Alpha",     high:true},
    {key:"drawdown_3y", label:"回撤控制",  high:false},
  ];

  const norm = (key, val, high) => {
    const vals = funds.map(f => f[key]??0);
    const min = Math.min(...vals), max = Math.max(...vals);
    if (max === min) return 50;
    const pct = (val - min) / (max - min) * 100;
    return high ? pct : 100 - pct;
  };

  const radarData = DIMS.map(m => {
    const pt = { metric: m.label };
    if (zoFund) pt["zo"] = norm(m.key, zoFund[m.key]??0, m.high);
    selFunds.forEach(f => { pt[f.code] = norm(m.key, f[m.key]??0, m.high); });
    return pt;
  });

  const legend = [
    {code:"zo", name:"中欧增强回报", color:"#D4AA4F"},
    ...selFunds.map((f,i) => ({code:f.code, name:f.name, color:COLORS[i+1]||COLORS[i]}))
  ];

  return (
    <ResponsiveContainer width="100%" height={260}>
      <RadarChart data={radarData}>
        <PolarGrid stroke="rgba(212,170,79,0.08)"/>
        <PolarAngleAxis dataKey="metric" tick={{fontSize:10,fill:"var(--dim)"}}/>
        <PolarRadiusAxis domain={[0,100]} tick={false} axisLine={false}/>
        <Radar dataKey="zo" name="中欧增强回报" stroke="#D4AA4F" fill="#D4AA4F"
          fillOpacity={0.18} strokeWidth={2.5}/>
        {selFunds.map((f,i) => (
          <Radar key={f.code} dataKey={f.code} name={f.name}
            stroke={COLORS[i+1]||COLORS[i]} fill={COLORS[i+1]||COLORS[i]}
            fillOpacity={0.08} strokeWidth={1.5}/>
        ))}
        <Legend formatter={v => legend.find(l=>l.code===v)?.name||v} wrapperStyle={{fontSize:10}}/>
        <Tooltip
          contentStyle={{background:"rgba(9,20,40,0.95)",border:"1px solid var(--border)",borderRadius:6,fontSize:10}}
          formatter={(v,n) => [`${v?.toFixed(0)}分`, legend.find(l=>l.code===n)?.name||n]}/>
      </RadarChart>
    </ResponsiveContainer>
  );
}

// ─── 全量数据表 ────────────────────────────────────────────────────────────
function PeerTable({ funds, selected, onSelect }) {
  const [sortKey, setSortKey] = useState("sharpe_3y");
  const [sortDir, setSortDir] = useState("desc");

  const COLS = [
    {key:"name",         label:"基金简称",   fmt:v=>v,    w:110, align:"left",  high:null},
    {key:"return_1y",    label:"近1年收益",  fmt:fPct,    w:72,  align:"right", high:true},
    {key:"return_3y",    label:"近3年收益",  fmt:fPct,    w:72,  align:"right", high:true},
    {key:"drawdown_3y",  label:"3年最大回撤",fmt:fPct,    w:82,  align:"right", high:false},
    {key:"volatility_3y",label:"3年波动率",  fmt:fPct,    w:76,  align:"right", high:false},
    {key:"sharpe_3y",    label:"3年夏普",    fmt:f2,      w:66,  align:"right", high:true},
    {key:"sortino_3y",   label:"3年索提诺",  fmt:f2,      w:72,  align:"right", high:true},
    {key:"calmar_3y",    label:"3年卡尔玛",  fmt:f2,      w:72,  align:"right", high:true},
    {key:"alpha_3y",     label:"3年Alpha",   fmt:f2,      w:66,  align:"right", high:true},
    {key:"info_3y",      label:"3年信息比率",fmt:f2,      w:76,  align:"right", high:true},
  ];

  const isLowBetter = key => key.includes("drawdown") || key.includes("volatility");

  const sorted = useMemo(() => {
    return [...funds].sort((a, b) => {
      const av = a[sortKey] ?? -Infinity;
      const bv = b[sortKey] ?? -Infinity;
      if (isLowBetter(sortKey)) {
        // 回撤/波动：desc=绝对值最小排前
        return sortDir==="desc" ? Math.abs(av)-Math.abs(bv) : Math.abs(bv)-Math.abs(av);
      }
      return sortDir==="desc" ? bv-av : av-bv;
    });
  }, [funds, sortKey, sortDir]);

  // 每列最优值
  const bestVals = useMemo(() => {
    const b = {};
    COLS.filter(c => c.high !== null && c.key !== "name").forEach(c => {
      const vals = funds.map(f => f[c.key]).filter(v => v != null);
      b[c.key] = c.high
        ? Math.max(...vals)
        : vals.reduce((a,v) => Math.abs(v)<Math.abs(a)?v:a, vals[0]);
    });
    return b;
  }, [funds]);

  const onSort = k => {
    if (k==="name") return;
    if (k===sortKey) setSortDir(d => d==="asc"?"desc":"asc");
    else { setSortKey(k); setSortDir("desc"); }
  };

  return (
    <div style={{overflowX:"auto"}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
        <thead>
          <tr style={{borderBottom:"1px solid var(--border)"}}>
            <th style={{width:26,padding:"6px 8px"}}/>
            {COLS.map(c => (
              <th key={c.key} onClick={()=>onSort(c.key)} style={{
                padding:"7px 8px",textAlign:c.align,whiteSpace:"nowrap",
                cursor:c.key!=="name"?"pointer":"default",width:c.w,
                color:sortKey===c.key?"var(--gold)":"var(--dim)",
                fontWeight:600,fontSize:10,
                borderBottom:sortKey===c.key?"1px solid var(--gold)":"none",
              }}>
                {c.label}{sortKey===c.key?(sortDir==="desc"?" ↓":" ↑"):""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((f, i) => {
            const isSel  = selected.includes(f.code) && !f.is_zo;
            const selIdx = selected.indexOf(f.code);
            return (
              <tr key={f.code} onClick={()=>onSelect(f.code)} style={{
                cursor:"pointer",
                background: f.is_zo  ? "rgba(212,170,79,0.05)"
                  : isSel ? "rgba(74,144,217,0.05)"
                  : i%2 ? "rgba(27,54,100,0.08)" : "transparent",
                borderLeft: f.is_zo ? "3px solid #D4AA4F"
                  : isSel ? `3px solid ${COLORS[selIdx%COLORS.length]}`
                  : "3px solid transparent",
              }}>
                <td style={{padding:"6px 8px",textAlign:"center"}}>
                  {!f.is_zo && (
                    <div style={{width:13,height:13,borderRadius:3,border:"1px solid",margin:"auto",
                      fontSize:8,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,
                      borderColor:isSel?COLORS[selIdx%COLORS.length]:"var(--border)",
                      background:isSel?COLORS[selIdx%COLORS.length]:"transparent",
                      color:"#091428"}}>
                      {isSel?"✓":""}
                    </div>
                  )}
                </td>
                {COLS.map(c => {
                  const val    = f[c.key];
                  const isBest = c.high!==null && c.key!=="name" && val!=null && val===bestVals[c.key];
                  return (
                    <td key={c.key} style={{
                      padding:"6px 8px",textAlign:c.align,
                      fontVariantNumeric:"tabular-nums",
                      fontWeight:isBest||f.is_zo?700:400,
                      color: f.is_zo&&c.key==="name" ? "var(--gold)"
                        : isBest ? "var(--gold)"
                        : "var(--text)",
                      fontSize:11,
                    }}>
                      {c.key==="name"
                        ? <>{val}{f.is_zo&&<span style={{fontSize:8,marginLeft:4,
                            background:"rgba(212,170,79,0.15)",color:"var(--gold)",
                            borderRadius:3,padding:"1px 4px"}}>中欧</span>}</>
                        : c.fmt(val)
                      }
                      {isBest && c.key!=="name" && (
                        <span style={{fontSize:8,marginLeft:2,color:"var(--gold)"}}>▲</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── MAIN APP ──────────────────────────────────────────────────────────────
export default function App() {
  const { data, navData, loading, error } = useOSSData();

  const [activeTab,     setActiveTab]     = useState("highlights");
  const [activeMetric,  setActiveMetric]  = useState("sharpe_3y");
  const [selectedPeers, setSelectedPeers] = useState([]);

  const funds = data?.funds || [];
  const zo    = data?.zhongou || null;

  const onSelect = code => {
    const f = funds.find(f => f.code === code);
    if (!f || f.is_zo) return;
    setSelectedPeers(p => p.includes(code) ? p.filter(c=>c!==code) : p.length>=4 ? p : [...p,code]);
  };

  const TABS = [
    {id:"highlights", label:"优势指标"},
    {id:"nav",        label:"净值走势"},
    {id:"radar",      label:"多维雷达"},
    {id:"ranking",    label:"行业排行"},
  ];

  const RANK_METRICS = [
    {key:"sharpe_3y",   label:"3年夏普",    high:true},
    {key:"sortino_3y",  label:"3年索提诺",  high:true},
    {key:"calmar_3y",   label:"3年卡尔玛",  high:true},
    {key:"return_3y",   label:"3年收益率",  high:true},
    {key:"alpha_3y",    label:"3年Alpha",   high:true},
    {key:"drawdown_3y", label:"3年最大回撤", high:false},
  ];

  return (
    <div style={{...CSS_VARS,
      fontFamily:"'PingFang SC','Microsoft YaHei',sans-serif",
      background:"#091428",color:"#E8E6E1",minHeight:"100vh",padding:0}}>

      <div style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:0,
        backgroundImage:"radial-gradient(ellipse at 15% 0%,rgba(27,54,100,0.5) 0%,transparent 55%)"}}/>

      <div style={{position:"relative",zIndex:1,maxWidth:1200,margin:"0 auto",padding:"24px 20px"}}>

        {/* Header */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
          marginBottom:20,flexWrap:"wrap",gap:10}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:36,height:36,borderRadius:8,flexShrink:0,
              background:"linear-gradient(135deg,#1B3664,#D4AA4F)",
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:16,fontWeight:900,color:"white"}}>中</div>
            <div>
              <div style={{fontSize:16,fontWeight:700}}>一级债基竞品分析</div>
              <div style={{fontSize:11,color:"var(--dim)"}}>中欧增强回报债券(LOF) · 行业对标研究</div>
            </div>
          </div>
          <div style={{fontSize:10,color:"var(--dim)",padding:"4px 12px",borderRadius:4,
            border:"1px solid var(--border)",background:"var(--surface)"}}>
            {data
              ? `数据来源 Wind · ${data.updated_at} · ${funds.length}只同类基金`
              : "数据加载中…"}
          </div>
        </div>

        {loading && <LoadingScreen/>}
        {error   && <ErrorScreen msg={error}/>}

        {!loading && !error && data && (<>

          {/* 广告卡 */}
          <AdCard zo={zo}/>

          {/* 分析区 */}
          <SectionCard title="竞品分析"
            action={
              <div style={{display:"flex",gap:2,background:"rgba(27,54,100,0.4)",
                borderRadius:5,padding:2,border:"1px solid var(--border)"}}>
                {TABS.map(t => (
                  <button key={t.id} onClick={()=>setActiveTab(t.id)} style={{
                    padding:"5px 14px",borderRadius:3,border:"none",fontSize:11,cursor:"pointer",
                    background:activeTab===t.id?"var(--gold)":"transparent",
                    color:activeTab===t.id?"#091428":"var(--dim)",
                    fontWeight:activeTab===t.id?700:400,
                  }}>{t.label}</button>
                ))}
              </div>
            }>

            {activeTab==="highlights" && (
              <div>
                <div style={{fontSize:11,color:"var(--dim)",marginBottom:12}}>
                  基于当前数据自动计算排名 · 金色=行业第1 · 每日数据更新后自动重新评估
                </div>
                <HighlightGrid highlights={zo?.highlights}/>
              </div>
            )}

            {activeTab==="nav" && (
              <div>
                <div style={{fontSize:11,color:"var(--dim)",marginBottom:12}}>
                  从下方列表勾选竞品加入对比（最多4只）· 中欧金色线默认展示
                </div>
                <NavChart navData={navData} funds={funds} selected={selectedPeers}/>
              </div>
            )}

            {activeTab==="radar" && (
              <div>
                <div style={{fontSize:11,color:"var(--dim)",marginBottom:12}}>
                  从下方列表勾选竞品对比（最多4只）· 回撤维度已取反，越靠外=控制越好
                </div>
                <MetricRadar funds={funds} selected={selectedPeers}/>
              </div>
            )}

            {activeTab==="ranking" && (
              <div>
                <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
                  {RANK_METRICS.map(m => (
                    <button key={m.key} onClick={()=>setActiveMetric(m.key)} style={{
                      padding:"4px 12px",borderRadius:4,fontSize:11,cursor:"pointer",fontWeight:500,
                      border:activeMetric===m.key?"1px solid var(--gold)":"1px solid var(--border)",
                      background:activeMetric===m.key?"rgba(212,170,79,0.1)":"transparent",
                      color:activeMetric===m.key?"var(--gold)":"var(--dim)",
                    }}>{m.label}</button>
                  ))}
                </div>
                <div style={{fontSize:11,color:"var(--dim)",marginBottom:10}}>
                  🟡 金色柱 = 中欧增强回报
                  {activeMetric.includes("drawdown") && " · 取绝对值展示，柱越短越好"}
                </div>
                <PeerBar
                  metric={activeMetric}
                  label={RANK_METRICS.find(m=>m.key===activeMetric)?.label}
                  funds={funds}
                  higherBetter={RANK_METRICS.find(m=>m.key===activeMetric)?.high}
                />
              </div>
            )}
          </SectionCard>

          {/* 全量数据表 */}
          <SectionCard title={`全量数据对比 · 点击行加入对比（最多4只）${selectedPeers.length>0?" · 已选："+funds.filter(f=>selectedPeers.includes(f.code)).map(f=>f.name).join("、"):""}`}>
            <div style={{fontSize:11,color:"var(--dim)",marginBottom:10}}>
              金色行=中欧增强回报 · ▲=该指标行业最优 · 点击列头排序
              {selectedPeers.length>0 && (
                <button onClick={()=>setSelectedPeers([])} style={{
                  marginLeft:10,padding:"2px 8px",borderRadius:3,fontSize:10,cursor:"pointer",
                  border:"1px solid rgba(224,123,84,0.3)",background:"rgba(224,123,84,0.06)",
                  color:"var(--red)"}}>
                  清空选择
                </button>
              )}
            </div>
            <PeerTable funds={funds} selected={selectedPeers} onSelect={onSelect}/>
          </SectionCard>

          {/* 页脚 */}
          <div style={{marginTop:12,paddingTop:14,borderTop:"1px solid var(--border)",
            display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
            <div style={{fontSize:10,color:"var(--dim)"}}>
              数据来源：Wind · 仅供内部研究参考，不构成投资建议
            </div>
            <div style={{fontSize:10,color:"var(--dim)"}}>
              © {new Date().getFullYear()} 中欧基金管理有限公司
            </div>
          </div>

        </>)}
      </div>
    </div>
  );
}
